import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

/**
 * ACH Form for Authorize.net
 * Collects bank account information directly (routing number, account number)
 * This is used when the customer has an ACH mandate in their contract
 */
const AchFormAuthorizenet = ({ prorated, captchaValue, previousChargedAmount = 0 }) => {
    const navigate = useNavigate();
    const { lead } = useSelector((state) => state.leads);

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        nameOnAccount: `${lead?.firstname || ""} ${lead?.lastname || ""}`.trim(),
        bankName: "",
        routingNumber: "",
        accountNumber: "",
        confirmAccountNumber: "",
        accountType: "checking",
        agreed: false,
    });
    const [errors, setErrors] = useState({});

    const totalOnetimeFees = lead?.onetime_fees?.reduce(
        (acc, curr) => (acc += parseFloat(curr.feeAmount)),
        0
    );

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nameOnAccount.trim()) {
            newErrors.nameOnAccount = "Name on account is required";
        }

        if (!formData.bankName.trim()) {
            newErrors.bankName = "Bank name is required";
        }

        if (!formData.routingNumber.trim()) {
            newErrors.routingNumber = "Routing number is required";
        } else if (!/^\d{9}$/.test(formData.routingNumber)) {
            newErrors.routingNumber = "Routing number must be exactly 9 digits";
        }

        if (!formData.accountNumber.trim()) {
            newErrors.accountNumber = "Account number is required";
        } else if (!/^\d{4,17}$/.test(formData.accountNumber)) {
            newErrors.accountNumber = "Account number must be 4-17 digits";
        }

        if (formData.accountNumber !== formData.confirmAccountNumber) {
            newErrors.confirmAccountNumber = "Account numbers do not match";
        }

        if (!formData.agreed) {
            newErrors.agreed = "You must agree to the terms and ACH authorization";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // TODO: Add captcha
        // if (!captchaValue) {
        //     toast.error("Please complete the captcha");
        //     return;
        // }

        if (!validateForm()) {
            toast.error("Please correct the errors in the form");
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}authorizenet/ach`,
                {
                    // Lead info
                    email: lead?.email,
                    lead_name: `${lead?.firstname} ${lead?.lastname}`,
                    lead: lead?._id,
                    manager: lead?.manager?._id,

                    // Payment schedule info
                    recurring_date: lead?.billing_date,
                    contract_length: lead?.contract_length,
                    one_time_fees: totalOnetimeFees || 0,
                    prorated: prorated || 0,
                    amount: lead?.package?.amount,
                    phase2_amount: lead?.package?.amount, // Same amount for phase 2
                    selected_method: lead?.paymentMethod,

                    // Bank account info
                    bankName: formData.bankName,
                    routingNumber: formData.routingNumber,
                    accountNumber: formData.accountNumber,
                    accountType: formData.accountType,
                    nameOnAccount: formData.nameOnAccount,

                    // Address info from lead
                    address: lead?.street,
                    city: lead?.city,
                    state: lead?.state,
                    zip: lead?.zipCode || lead?.zipcode,

                    // Previous card charge amount (only relevant for 'both' payment method)
                    previousChargedAmount: previousChargedAmount || 0,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {
                toast.success("Payment setup successful!");
                const exactAmount = response.data.data?.initialPaymentAmount || previousChargedAmount || 0;
                navigate(`/payment/${lead?._id}/success?amount=${exactAmount}`);
            } else {
                toast.error(response.data.message || "Payment failed");
            }
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error?.message ||
                error?.message ||
                "An error occurred. Please try again.";
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <form onSubmit={handleSubmit}>
                <div className="rounded-md w-full p-6 mx-auto lg:w-2/3">
                    {/* Bank Information Header */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            <i className="ri-bank-line mr-2"></i>
                            Bank Account Information
                        </h3>
                        <p className="text-sm text-gray-500">
                            Your payment will be processed via ACH direct debit. By providing
                            your bank information, you authorize recurring payments as outlined
                            in your contract.
                        </p>
                    </div>

                    {/* Name on Account */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Name on Account <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="nameOnAccount"
                            value={formData.nameOnAccount}
                            onChange={handleChange}
                            placeholder="Enter name exactly as it appears on your bank account"
                            className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
                        />
                        {errors.nameOnAccount && (
                            <span className="text-red-600 text-sm">
                                {errors.nameOnAccount}
                            </span>
                        )}
                    </div>

                    {/* Bank Name */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Bank Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="bankName"
                            value={formData.bankName}
                            onChange={handleChange}
                            placeholder="e.g. Chase, Wells Fargo, Bank of America"
                            className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
                        />
                        {errors.bankName && (
                            <span className="text-red-600 text-sm">
                                {errors.bankName}
                            </span>
                        )}
                    </div>

                    {/* Account Type */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Account Type <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="accountType"
                                    value="checking"
                                    checked={formData.accountType === "checking"}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-[#0E669B]"
                                />
                                <span className="text-gray-700">Checking</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="accountType"
                                    value="savings"
                                    checked={formData.accountType === "savings"}
                                    onChange={handleChange}
                                    className="w-4 h-4 text-[#0E669B]"
                                />
                                <span className="text-gray-700">Savings</span>
                            </label>
                        </div>
                    </div>

                    {/* Routing Number */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Routing Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="routingNumber"
                            value={formData.routingNumber}
                            onChange={handleChange}
                            placeholder="9-digit routing number"
                            maxLength={9}
                            className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
                        />
                        {errors.routingNumber && (
                            <span className="text-red-600 text-sm">{errors.routingNumber}</span>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                            Found at the bottom left of your check
                        </p>
                    </div>

                    {/* Account Number */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleChange}
                            placeholder="Enter your account number"
                            maxLength={17}
                            className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
                        />
                        {errors.accountNumber && (
                            <span className="text-red-600 text-sm">{errors.accountNumber}</span>
                        )}
                    </div>

                    {/* Confirm Account Number */}
                    <div className="my-4">
                        <label className="mb-2 block text-base font-medium text-[#07074D]">
                            Confirm Account Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="confirmAccountNumber"
                            value={formData.confirmAccountNumber}
                            onChange={handleChange}
                            placeholder="Re-enter your account number"
                            maxLength={17}
                            className="w-full rounded-md border border-[#e0e0e0] bg-white py-3 px-6 text-base font-medium text-[#6B7280] outline-none focus:border-[#003049] focus:shadow-md"
                        />
                        {errors.confirmAccountNumber && (
                            <span className="text-red-600 text-sm">
                                {errors.confirmAccountNumber}
                            </span>
                        )}
                    </div>

                    {/* Check Image Guide */}
                    <div className="my-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-sm text-gray-600 text-center">
                            <i className="ri-information-line mr-1"></i>
                            Your routing and account numbers can be found at the bottom of your
                            check
                        </p>
                    </div>

                    {/* Tax Notice */}
                    <div className="flex items-center">
                        <span className="text-red-500 my-4">
                            NB: State and Local taxes applicable (8.25% will be added to all
                            charges)
                        </span>
                    </div>

                    {/* ACH Authorization Agreement */}
                    <div className="my-4 p-4 bg-blue-50 rounded-md border border-blue-200">
                        <div className="flex items-start">
                            <input
                                type="checkbox"
                                name="agreed"
                                checked={formData.agreed}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-[#0E669B] mr-3 mt-0.5"
                            />
                            <div>
                                <span className="text-gray-700 text-sm">
                                    I authorize SavvyCams to initiate ACH debit entries from the bank
                                    account provided above for the payment of services as outlined in
                                    my contract. I understand that this authorization will remain in
                                    effect until I notify SavvyCams in writing to cancel it.
                                </span>
                            </div>
                        </div>
                        {errors.agreed && (
                            <span className="text-red-600 text-sm block mt-2">
                                {errors.agreed}
                            </span>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="my-4 mx-auto lg:w-2/3 flex justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        type="button"
                        className="block text-center uppercase bg-white hover:bg-gray-100 focus:shadow-outline focus:outline-none text-[#0E669B] text-xs py-3 px-10 rounded border border-[#0E669B]"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="block text-center uppercase shadow bg-[#0E669B] hover:bg-[#0a5580] focus:shadow-outline focus:outline-none text-white text-xs py-3 px-10 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <span className="flex items-center gap-2">
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                Processing...
                            </span>
                        ) : (
                            "Submit Payment"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AchFormAuthorizenet;
